import { useQuery } from "@tanstack/react-query";
import { getServicesCategories } from "api/main";
import Popper from "components/General/Common/Popper";
import { CustomButton } from "components/General/CustomButton";
import { LanguageSelector } from "components/General/LanguageSelector";
import { TextSkeleton } from "components/TextSkeleton";
import useSetting from "hooks/useSetting";
import { useTranslation } from "next-i18next";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useState } from "react";
import OutsideClickHandler from "utils/OutsideClickHandler";
import { getHeaderItems } from "utils/getHeaderItems";
import MobileDrawer from "./MobileDrawer";

function Header({ isCRDC }: { isCRDC: boolean }) {
  const [shouldShowDrawer, setShouldShowDrawer] = useState(false);
  const { data } = useSetting(isCRDC);
  const router = useRouter();
  const { locale } = router;
  const { t } = useTranslation("common");

  const { data: servicesCategories } = useQuery(["getServicesCategories", locale], () =>
    getServicesCategories(locale)
  );

  return (
    <>
      <div className="fixed left-0 right-0 top-0 z-2">
        <div className=" z-10 bg-secondary-main ">
          <div className="container py-12">
            <div className="hidden w-full items-center justify-between md:flex">
              <div className="text-16 text-white">{data?.slogan}</div>
              <LanguageSelector />
            </div>

            <div className="flex w-full items-center justify-between md:hidden">
              <div onClick={() => setShouldShowDrawer(true)}>
                <Image
                  src="/assets/Union.svg"
                  alt="union"
                  width={24}
                  height={24}
                  className="icon-menu text-18 text-white"
                />
              </div>
              <Link passHref href={isCRDC ? "/research" : "/"} className="flex items-center">
                <Image
                  src={isCRDC ? "/assets/logo-tahgig.png" : "/assets/logo.png"}
                  width="81"
                  height={"30"}
                  alt="لوگو بیمارستان ولیعصر"
                />
              </Link>
            </div>
          </div>
        </div>

        <div className="hidden bg-white py-20 md:block">
          <div className="container">
            <div className="hidden w-full items-center justify-between md:flex">
              <div className="flex items-center">
                <Link passHref href={isCRDC ? "/research" : "/"} className="flex items-center">
                  <Image
                    src={isCRDC ? "/assets/logo-tahgig.png" : "/assets/logo.png"}
                    width="120"
                    height={"30"}
                    alt="لوگو بیمارستان ولیعصر"
                  />
                </Link>

                <div className="ms-32 flex items-center text-15 leading-28 text-gray-151618">
                  {getHeaderItems(isCRDC, t, servicesCategories?.data, locale)?.map((el, i) => {
                    return el?.path ? (
                      <Link
                        prefetch={false}
                        key={i}
                        href={el?.path}
                        passHref
                        className={`relative text-gray-151618 before:absolute before:bottom-[-8px] before:h-4 before:w-0 before:rounded-8 before:bg-primary-main before:duration-500 before:ease-in-out hover:before:w-[50%] hover:before:content-[''] ltr:me-20 ltr:text-11 rtl:me-20 rtl:text-12`}
                      >
                        {el?.title}
                      </Link>
                    ) : (
                      el?.children && (
                        <Popper
                          noClick={el?.children?.length === 1}
                          title={
                            <p
                              className={`text-gray-151618 ltr:me-20 ltr:text-10 rtl:me-20 rtl:text-12`}
                            >
                              {el.title}

                              <span className="inline-block ltr:ml-6 ltr:rotate-90 rtl:mr-4 rtl:-rotate-90">
                                {" "}
                                {">"}{" "}
                              </span>
                              {/*  */}
                            </p>
                          }
                        >
                          <div className="flex flex-col">
                            {el?.children.length === 0 && (
                              <div className="flex flex-col gap-10">
                                {[...Array(3)].map((item, index) => (
                                  <TextSkeleton key={index} />
                                ))}
                              </div>
                            )}

                            {el?.children?.map((el2: any, i2: number) => {
                              return el2?.path ? (
                                <Link
                                  prefetch={false}
                                  key={i}
                                  href={el2?.path}
                                  passHref
                                  className={` text-12 text-gray-151618  ltr:me-24 rtl:me-20`}
                                >
                                  {el2?.title}
                                </Link>
                              ) : (
                                el2?.children && (
                                  <Popper
                                    leftHeader
                                    noHover
                                    arrow
                                    title={
                                      <p className={`text-14 text-gray-151618 ltr:me-24 rtl:me-20`}>
                                        {el2?.title}
                                      </p>
                                    }
                                  >
                                    <div className="flex flex-col">
                                      {el2?.children?.map((el3: any, i3: number) => {
                                        return (
                                          <Link
                                            prefetch={false}
                                            key={i3}
                                            href={el3?.path}
                                            className="mt-4 text-14  text-gray-151618"
                                          >
                                            {el3?.title}
                                          </Link>
                                        );
                                      })}
                                    </div>
                                  </Popper>
                                )
                              );
                            })}
                          </div>
                        </Popper>
                      )
                    );
                  })}
                </div>
              </div>
              {isCRDC ? (
                <CustomButton
                  className="!rounded-full ltr:!px-8 ltr:!text-12"
                  onClick={() => {
                    if (router.pathname !== "/research") {
                      router.back();
                    } else {
                      router.push("/");
                    }
                  }}
                >
                  {router.pathname !== "/research" ? t("BackPre") : t("Back")}
                </CustomButton>
              ) : (
                <div className="flex flex-col items-center gap-12 lg:flex-row">
                  {locale === "fa" && (
                    <CustomButton
                      className="text-nowrap !rounded-full !px-16 md:text-10"
                      href="https://vht-app.ir"
                    >
                      {t("DoctorsAppointment")}
                    </CustomButton>
                  )}
                  <CustomButton
                    variant="outlined"
                    className="text-nowrap !rounded-full !px-1 md:text-10"
                    onClick={() => {
                      router.push("visitors-guide/doctors");
                    }}
                  >
                    {t("Doctors")}
                  </CustomButton>
                  {/* <CustomButton
                    variant="outlined"
                    className="!rounded-full !px-1 md:text-10"
                    onClick={() => {
                      router.push("http://2.186.118.90:9009/default.aspx?ReturnUrl=%2f");
                    }}
                  >
                    {t("DoctorsSignIn")}
                  </CustomButton> */}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <OutsideClickHandler handleClick={() => setShouldShowDrawer(false)}>
        <MobileDrawer
          open={shouldShowDrawer}
          isCRDC={isCRDC}
          onClose={() => setShouldShowDrawer(false)}
          t={t}
        />
      </OutsideClickHandler>
    </>
  );
}

export default Header;
